/**
 * Rango Quantum — adapter: traduce la config Quantum a los parámetros del
 * motor (engine.js), calcula señales + niveles y camina las velas
 * posteriores a cada señal para determinar su outcome (TP1/TP2/liquidación/
 * activa) — mismas fórmulas y misma prioridad same-candle que
 * backend/research/rango_quantum/simulator.py (liquidación/SL manda si se
 * toca a la vez que un TP; ver ese módulo para el razonamiento geométrico
 * completo). Es un cálculo de PRESENTACIÓN (para pintar el chart y el
 * dashboard) — la contabilidad real de PnL vive en el motor Python/backend.
 *
 * Nunca lanza excepción: cualquier entrada inválida devuelve la salida vacía.
 */
import { computeSignalFrame, extractSignals, WARMUP_BARS } from './engine.js'
import { applyProductionFilters } from './filters.js'

function emptyOutput() {
  return { signals: [], activeSignal: null, lastSignal: null }
}

function slPriceFor(entryPrice, atrAtSignal, direction, leverage, cfg) {
  const dirn = direction === 'long' ? 1 : -1
  if (cfg.slMode === 'pct') {
    const offset = (cfg.slPct / 100) * entryPrice
    return entryPrice - dirn * offset
  }
  if (cfg.slMode === 'atr') {
    const offset = cfg.slAtr * atrAtSignal
    return entryPrice - dirn * offset
  }
  // 'liquidation': sin SL propio — la liquidación por apalancamiento actúa de stop.
  const mmr = 0.01
  const dist = Math.max(1 / leverage - mmr, 0.001)
  return dirn === 1 ? entryPrice * (1 - dist) : entryPrice * (1 + dist)
}

function liqPriceFor(entryPrice, direction, leverage, mmr = 0.01) {
  const dirn = direction === 'long' ? 1 : -1
  const dist = Math.max(1 / leverage - mmr, 0.001)
  return dirn === 1 ? entryPrice * (1 - dist) : entryPrice * (1 + dist)
}

function tpPricesFor(entryPrice, atrAtSignal, direction, cfg) {
  const dirn = direction === 'long' ? 1 : -1
  return [
    entryPrice + dirn * cfg.tp1DistanceAtr * atrAtSignal,
    entryPrice + dirn * cfg.tp2DistanceAtr * atrAtSignal,
  ]
}

// `candle.time` puede llegar en segundos o en ms según la fuente (mismo
// heurístico ya usado en otros indicadores del registro — showict_quantum/
// adapter.js, etc. — D.3 del Standard: cleanTime()/normalización en TODA
// comparación de tiempos, nunca mezclar unidades).
function toSeconds(t) {
  if (t == null || Number.isNaN(t)) return null
  return t > 1e10 ? Math.floor(t / 1000) : t
}

/**
 * Time-stop de serie (Fase 0.6, decisión de Marc 2026-08-15): si la
 * posición lleva >= cfg.timeStopDays días naturales desde la vela de señal
 * sin haber tocado TP ni liquidación/SL, se cierra a mercado al cierre de
 * la vela candidata. `cfg.timeStopDays === 0` desactiva el time-stop por
 * completo (motor idéntico bit a bit al comportamiento previo a esta
 * feature) — nunca se interpreta como "dispara con 0 días transcurridos".
 *
 * `signal.time`/`candle.time` son el OPEN time de sus respectivas velas
 * (no el close) — igual que en el motor Python
 * (research/rango_quantum/simulator.py::TimeStop.should_close), esto NO es
 * un bug: la entrada real ocurre al close de la vela de señal (= open de
 * la siguiente, un `tf` más tarde que `signal.time`) y el cierre por
 * time-stop ejecuta al close de la vela candidata (= `candle.time` + `tf`)
 * — los dos desfases de `tf` se cancelan, así que comparar directamente
 * los OPEN times da el elapsed-time real correcto. Asume velas contiguas
 * sin huecos.
 */
function timeStopElapsedDays(signalTime, candleTime) {
  const s = toSeconds(signalTime)
  const c = toSeconds(candleTime)
  if (s == null || c == null) return -Infinity
  return (c - s) / 86400
}

/**
 * Resuelve el outcome de una señal caminando las velas estrictamente
 * posteriores a la de disparo. Orden de evaluación worst-case dentro de la
 * misma vela (idéntico al motor Python, ver
 * research/rango_quantum/simulator.py y docs/QUANTUM_STYLE_STANDARD.md
 * Parte J): 1) liquidación/SL intrabar — gana siempre, sin ambigüedad; 2)
 * TP1/TP2 intrabar — un toque de high/low ocurre DURANTE la formación de
 * la vela; 3) time-stop al cierre — cronológicamente posterior a
 * cualquier toque intrabar de TP, solo se evalúa si la posición sigue
 * abierta tras 1 y 2 (si TP2 tocó esta misma vela, ya se retornó arriba).
 * El SL configurado por el usuario (modo 'pct' o 'atr') manda sobre la
 * liquidación cuando está más cerca del precio de entrada que ella (mismo
 * razonamiento que simulator.py::ProtectiveSL); si no, se ignora para esa
 * señal y decide la liquidación.
 */
export function resolveOutcome(signal, candles, signalIdx, cfg) {
  const direction = signal.direction
  const dirn = direction === 'long' ? 1 : -1
  const entry = signal.entryPrice
  const atr = signal.atr
  const [tp1, tp2] = tpPricesFor(entry, atr, direction, cfg)
  const liqPrice = liqPriceFor(entry, direction, cfg.leverage)

  let slPrice = null
  let slIsProtective = false
  if (cfg.slMode !== 'liquidation') {
    const candidate = slPriceFor(entry, atr, direction, cfg.leverage, cfg)
    const nearerThanLiq = dirn === 1 ? candidate > liqPrice : candidate < liqPrice
    if (nearerThanLiq) {
      slPrice = candidate
      slIsProtective = true
    }
  }

  const out = {
    ...signal,
    tp1, tp2, liqPrice, slPrice: slIsProtective ? slPrice : null,
    tp1Hit: false, tp2Hit: false, outcome: 'active', outcomeTime: null,
  }

  for (let i = signalIdx + 1; i < candles.length; i++) {
    const { high, low } = candles[i]
    const stopPrice = slIsProtective ? slPrice : liqPrice
    const stopHit = dirn === 1 ? low <= stopPrice : high >= stopPrice
    const tp1Touch = dirn === 1 ? high >= tp1 : low <= tp1
    const tp2Touch = dirn === 1 ? high >= tp2 : low <= tp2

    if (stopHit) {
      out.outcome = slIsProtective ? 'sl' : 'liquidated'
      out.outcomeTime = candles[i].time
      return out
    }
    if (!out.tp1Hit && tp1Touch) {
      out.tp1Hit = true
    }
    if (tp2Touch) {
      out.tp2Hit = true
      out.outcome = 'tp2'
      out.outcomeTime = candles[i].time
      return out
    }
    if (cfg.timeStopDays > 0 && timeStopElapsedDays(signal.time, candles[i].time) >= cfg.timeStopDays) {
      out.outcome = 'time_stop'
      out.outcomeTime = candles[i].time
      out.timeStopExitPrice = candles[i].close
      return out
    }
  }
  return out
}

/**
 * @param {Array} candles velas del chart (mismo shape que engine.js espera).
 *   PUEDE incluir la vela en formación (última) como su elemento final — el
 *   caller (ChartPublicPage/usePublicChartData) no lo garantiza siempre: el
 *   polling REST de reconciliación devuelve el estado actual, que a veces
 *   incluye la vela viva aún sin cerrar (ver contrato documentado en
 *   hooks/useLiveCandle.js, que deliberadamente NO re-dispara este cálculo
 *   por cada tick, pero no puede evitar que el polling periódico sí lo haga
 *   mientras la última vela sigue abierta).
 * @param {Object} cfg config resuelta (resolveConfig)
 */
export function runAdapter(candles, cfg) {
  try {
    if (!Array.isArray(candles) || candles.length < WARMUP_BARS + 2) return emptyOutput()

    // Descarta la última vela antes de calcular NINGUNA señal ni resolver
    // NINGÚN outcome (0.A del estudio: "sin repintado" — un indicador solo
    // pinta señales sobre velas YA CERRADAS). Bug real, validación en
    // pantalla de Marc (2026-08-15): etiquetas "SHORT 63419.6" ancladas a la
    // última vela que aparecían y desaparecían unos segundos después — la
    // vela en curso cumplía longCond/shortCond de forma transitoria mientras
    // su precio se movía entre polls, y esa señal fantasma desaparecía en
    // cuanto el precio salía de la condición en el siguiente poll (nunca
    // persiste: cada `detect()` recalcula desde cero). Se corta la vela viva
    // de raíz aquí, no solo al pintar — así tampoco puede resolver el
    // outcome de una señal previa (TP/SL/liq tocado en la vela en curso es
    // igual de repintable que una entrada nueva).
    const closedCandles = candles.slice(0, -1)

    // WP H-G: el chart pinta lo que el bot operaría — mismos filtros de
    // producción (rsi_narrow + atr_min_median, mediana RODANTE de 2000 velas
    // por vela, ver filters.js) entre la condición base y las señales
    // pintadas. cfg.filters = [] pinta la condición base sin filtros (el
    // badge de divergencia lo avisa si el usuario tiene un bot vivo).
    const sf = applyProductionFilters(computeSignalFrame(closedCandles, WARMUP_BARS), cfg.filters)
    const rawSignals = extractSignals(sf)
    if (rawSignals.length === 0) return emptyOutput()

    const timeToIdx = new Map(closedCandles.map((c, i) => [c.time, i]))

    // Una posición abierta por símbolo/TF a la vez (0.B del estudio,
    // backend/research/rango_quantum/simulator.py::simulate_symbol_timeframe,
    // next_available_idx) — extractSignals() no lo aplica (es 1:1 con
    // signals.py, condición pura por vela, sin estado). Sin esta gate, dos
    // señales solapadas en el tiempo podían quedar AMBAS "active" (ninguna
    // tocó TP/SL/liq todavía) y activeSignal (más abajo) podía devolver la
    // más antigua — que puede no estar entre las últimas señales pintadas
    // en el chart (renderer.js solo dibuja las últimas 40). Con la gate,
    // como mucho hay una señal 'active' a la vez y siempre es la más
    // reciente — coherente por construcción con lo pintado.
    const signals = []
    let nextAvailableIdx = 0
    for (const sig of rawSignals) {
      const idx = timeToIdx.get(sig.time)
      if (idx == null) continue
      if (idx < nextAvailableIdx) continue
      const resolved = resolveOutcome(sig, closedCandles, idx, cfg)
      signals.push(resolved)
      if (resolved.outcomeTime != null) {
        const outcomeIdx = timeToIdx.get(resolved.outcomeTime)
        nextAvailableIdx = outcomeIdx != null ? outcomeIdx + 1 : idx + 1
      } else {
        // Sigue activa (nunca resolvió dentro del historial CERRADO
        // disponible): ninguna señal nueva se considera hasta que esta se
        // cierre.
        nextAvailableIdx = closedCandles.length
      }
    }

    const activeSignal = signals.find((s) => s.outcome === 'active') || null
    const lastSignal = signals[signals.length - 1] || null

    return { signals, activeSignal, lastSignal }
  } catch {
    return emptyOutput()
  }
}
